import React from "react";
import { NavLink, useNavigate, Link, useLocation } from "react-router-dom";
import {
  Lightning, House, Barbell, ForkKnife, MoonStars,
  Target, Brain, BookOpen, Books, Users, SignOut,
  Trophy, CalendarBlank, Medal, User, ChartLine, Sparkle, Camera,
  Briefcase, ListChecks, UsersFour, ChatCircleDots, VideoCamera, UsersThree, Buildings,
  ShieldStar, BowlFood, Ruler, CheckSquare, Note, Heartbeat, ClipboardText,
  CreditCard, FileText, Bandaids, Phone, List, X, Bell, Lock,
  Calendar, CurrencyDollar, Megaphone, Envelope, Gear,
} from "@phosphor-icons/react";
import { useAcademy } from "@/contexts/AcademyContext";
import { useAuth } from "@/contexts/AuthContext";
import { canonicalRole } from "@/lib/roles";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import KillswitchBanner from "@/components/KillswitchBanner";
import NotificationBell from "./NotificationBell";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const athleteNavItems = [
  { to: "/athlete", icon: Briefcase, label: "Athlete Hub", testid: "nav-athlete-hub", highlight: true },
  { to: "/athlete?tab=today", matchSearch: "tab=today", icon: CheckSquare, label: "Today", testid: "nav-athlete-today" },
  { to: "/athlete?tab=readiness", matchSearch: "tab=readiness", icon: Heartbeat, label: "Readiness", testid: "nav-athlete-readiness" },
  { to: "/athlete?tab=coach", matchSearch: "tab=coach", icon: ChatCircleDots, label: "Coach Chat", testid: "nav-athlete-coach-chat" },
  { to: "/athlete?tab=prs", matchSearch: "tab=prs", icon: Trophy, label: "PR Board", testid: "nav-athlete-pr-board" },
  { to: "/athlete?tab=wellness", matchSearch: "tab=wellness", icon: Ruler, label: "Wellness", testid: "nav-athlete-wellness" },
  { to: "/athlete?tab=profile", matchSearch: "tab=profile", icon: User, label: "Athlete Profile", testid: "nav-athlete-profile" },
  { to: "/dashboard", icon: House, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/workouts", icon: Barbell, label: "Workouts", testid: "nav-workouts" },
  { to: "/calendar", icon: CalendarBlank, label: "Calendar", testid: "nav-calendar" },
  { to: "/programs", icon: BookOpen, label: "Programs", testid: "nav-programs" },
  { to: "/form-check", icon: Camera, label: "Form Check", testid: "nav-form-check" },
  { to: "/exercises", icon: Books, label: "Exercises", testid: "nav-exercises" },
  { to: "/athlete/tests", icon: ClipboardText, label: "Test Library", testid: "nav-athlete-test-library" },
  { to: "/prs", icon: Trophy, label: "Records", testid: "nav-prs" },
  { to: "/nutrition", icon: ForkKnife, label: "Nutrition", testid: "nav-nutrition" },
  { to: "/recovery", icon: MoonStars, label: "Recovery", testid: "nav-recovery" },
  { to: "/goals", icon: Target, label: "Goals", testid: "nav-goals" },
  { to: "/analytics", icon: ChartLine, label: "Analytics", testid: "nav-analytics" },
  { to: "/achievements", icon: Medal, label: "Achievements", testid: "nav-achievements" },
];

// Individuals get pure self-training — including AI Coach for self-programming
const individualNavItems = [
  { to: "/solo", icon: Briefcase, label: "Solo Hub", testid: "nav-solo-hub", highlight: true },
  { to: "/solo?tab=today", matchSearch: "tab=today", icon: CheckSquare, label: "Today", testid: "nav-solo-today" },
  { to: "/solo?tab=habits", matchSearch: "tab=habits", icon: ListChecks, label: "Habits", testid: "nav-solo-habits" },
  { to: "/solo?tab=goals", matchSearch: "tab=goals", icon: Target, label: "Goals", testid: "nav-solo-goals" },
  { to: "/dashboard", icon: House, label: "Dashboard", testid: "nav-dashboard" },
  { to: "/workouts", icon: Barbell, label: "Workouts", testid: "nav-workouts" },
  { to: "/calendar", icon: CalendarBlank, label: "Calendar", testid: "nav-calendar" },
  { to: "/programs", icon: BookOpen, label: "Programs", testid: "nav-programs" },
  { to: "/ai-coach", icon: Brain, label: "AI Coach", testid: "nav-ai-coach" },
  { to: "/form-check", icon: Camera, label: "Form Check", testid: "nav-form-check" },
  { to: "/exercises", icon: Books, label: "Exercises", testid: "nav-exercises" },
  { to: "/prs", icon: Trophy, label: "Records", testid: "nav-prs" },
  { to: "/nutrition", icon: ForkKnife, label: "Nutrition", testid: "nav-nutrition" },
  { to: "/recovery", icon: MoonStars, label: "Recovery", testid: "nav-recovery" },
  { to: "/analytics", icon: ChartLine, label: "Analytics", testid: "nav-analytics" },
  { to: "/achievements", icon: Medal, label: "Achievements", testid: "nav-achievements" },
];

const coachNavItems = [
  { to: "/coach", icon: Briefcase, label: "Coach Hub", testid: "nav-coach-hub", highlight: true },
  { to: "/coach?tab=one-rm", matchSearch: "tab=one-rm", icon: Trophy, label: "1RM Board", testid: "nav-coach-1rm" },
  { to: "/coach?tab=load-calc", matchSearch: "tab=load-calc", icon: Sparkle, label: "Load Calculator", testid: "nav-coach-load-calc", scAConly: true },
  { to: "/coach?tab=autoreg", matchSearch: "tab=autoreg", icon: Heartbeat, label: "Autoregulation", testid: "nav-coach-autoreg", scAConly: true },
  { to: "/coach?tab=volume", matchSearch: "tab=volume", icon: ChartLine, label: "Volume Report", testid: "nav-coach-volume" },
  { to: "/coach?tab=templates", matchSearch: "tab=templates", icon: BookOpen, label: "Session Templates", testid: "nav-coach-templates" },
  { to: "/coach?tab=planner", matchSearch: "tab=planner", icon: CalendarBlank, label: "Deload/Peak", testid: "nav-coach-planner", scAConly: true },
  { to: "/coach/clients", icon: UsersFour, label: "Clients", testid: "nav-coach-clients" },
  { to: "/coach/performance", icon: ChartLine, label: "Performance", testid: "nav-coach-performance" },
  { to: "/coach/programs", icon: ListChecks, label: "Program Builder", testid: "nav-coach-programs" },
  { to: "/coach/exercises", icon: Barbell, label: "Exercise Library", testid: "nav-coach-exercise-library" },
  { to: "/coach/teams", icon: UsersThree, label: "Teams", testid: "nav-coach-teams" },
  { to: "/coach/test-library", icon: Books, label: "Test Library", testid: "nav-coach-test-library" },
  { to: "/calendar", icon: CalendarBlank, label: "Calendar", testid: "nav-coach-calendar" },
];

// Fitness Trainer persona focuses on general fitness / hypertrophy — S&C-only tools
// (Load Calculator, Autoregulation, Deload/Peak) are hidden. 1RM Board stays because
// it doubles as a load-anchor for hypertrophy programming.
const filterCoachNavForRole = (items, role) =>
  role === "fitness_trainer" ? items.filter((i) => !i.scAConly) : items;

// Academy admin coach nav — same as coach but WITHOUT Program Builder (they manage the org, not programs)
const academyAdminCoachNavItems = [
  { to: "/coach", icon: Briefcase, label: "Coach Hub", testid: "nav-coach-hub", highlight: true },
  { to: "/coach/clients", icon: UsersFour, label: "Clients", testid: "nav-coach-clients" },
  { to: "/coach/performance", icon: ChartLine, label: "Performance", testid: "nav-coach-performance" },
  { to: "/coach/teams", icon: UsersThree, label: "Teams", testid: "nav-coach-teams" },
];

// AI-assisted program builder — only for independent coaches, not academy coaches
const aiProgramBuilderItem = { to: "/ai-coach", icon: Brain, label: "AI Program Builder", testid: "nav-ai-program-builder" };

// Academy coaches only need to track their athletes — minimal toolkit
const academyCoachNavItems = [
  { to: "/coach", icon: Briefcase, label: "Coach Hub", testid: "nav-coach-hub", highlight: true },
  { to: "/coach/clients", icon: UsersFour, label: "My Athletes", testid: "nav-coach-clients" },
  { to: "/coach/teams", icon: UsersThree, label: "Teams", testid: "nav-coach-teams" },
];

const socialNavItems = [
  { to: "/discover", icon: Users, label: "Discover Coaches", testid: "nav-discover" },
  { to: "/social", icon: Users, label: "Feed", testid: "nav-social" },
];

// R3 — Role-specific nav arrays for new roles introduced in R1/R2
const fitnessTrainerNavItems = [
  { to: "/fitness-trainer",  icon: Briefcase,     label: "Trainer Hub",    testid: "nav-ft-hub", highlight: true },
  { to: "/coach/clients",    icon: UsersFour,     label: "Clients",        testid: "nav-ft-clients" },
  { to: "/programs",         icon: Barbell,       label: "Workout Plans",  testid: "nav-ft-plans" },
  { to: "/nutrition",        icon: BowlFood,      label: "Nutrition",      testid: "nav-ft-nutrition" },
  { to: "/dashboard",        icon: Ruler,         label: "Measurements",   testid: "nav-ft-measurements" },
  { to: "/social",           icon: Trophy,        label: "Challenges",     testid: "nav-ft-challenges" },
  { to: "/analytics",        icon: ChartLine,     label: "Reports",        testid: "nav-ft-reports" },
  { to: "/coach/clients",    icon: CheckSquare,   label: "Attendance",     testid: "nav-ft-attendance" },
];

const sportsCoachNavItems = [
  { to: "/sports-coach",                    icon: Briefcase,     label: "Technical Hub",     testid: "nav-sc-hub", highlight: true },
  { to: "/sports-coach?tab=programs",       matchSearch: "tab=programs",  icon: FileText,      label: "Programs",          testid: "nav-sc-programs" },
  { to: "/sports-coach?tab=drills",         matchSearch: "tab=drills",    icon: Barbell,       label: "Drills Library",    testid: "nav-sc-drills" },
  { to: "/sports-coach?tab=sessions",       matchSearch: "tab=sessions",  icon: Sparkle,       label: "Session Plans",     testid: "nav-sc-sessions" },
  { to: "/sports-coach?tab=analyzer",       matchSearch: "tab=analyzer",  icon: VideoCamera,   label: "Form Analyzer",     testid: "nav-sc-analyzer" },
  { to: "/sports-coach?tab=opponents",      matchSearch: "tab=opponents", icon: Note,          label: "Opposition Scouting", testid: "nav-sc-opponents" },
  { to: "/sports-coach?tab=matchday",       matchSearch: "tab=matchday",  icon: Trophy,        label: "Match-Day Playbook",  testid: "nav-sc-matchday" },
  { to: "/coach/clients",                   icon: UsersFour,    label: "Athletes",           testid: "nav-sc-athletes" },
  { to: "/coach/teams",                     icon: UsersThree,   label: "Teams",              testid: "nav-sc-teams" },
];

const parentNavItems = [
  { to: "/parent",                      icon: Briefcase,     label: "Family Hub",     testid: "nav-parent-hub", highlight: true },
  { to: "/parent?tab=athletes",         matchSearch: "tab=athletes",     icon: UsersFour,   label: "My Children",   testid: "nav-parent-athletes" },
  { to: "/parent?tab=attendance",       matchSearch: "tab=attendance",   icon: CheckSquare, label: "Attendance",    testid: "nav-parent-attendance" },
  { to: "/parent?tab=payments",         matchSearch: "tab=payments",     icon: CreditCard,  label: "Payments",      testid: "nav-parent-payments" },
  { to: "/parent?tab=competitions",     matchSearch: "tab=competitions", icon: CalendarBlank, label: "Competitions", testid: "nav-parent-competitions" },
  { to: "/parent?tab=reports",          matchSearch: "tab=reports",      icon: FileText,    label: "Reports",       testid: "nav-parent-reports" },
];

const generalClientNavItems = [
  { to: "/solo",         icon: Briefcase,     label: "Solo Hub",      testid: "nav-solo-hub", highlight: true },
  { to: "/solo?tab=today",  matchSearch: "tab=today",  icon: CheckSquare, label: "Today",  testid: "nav-solo-today" },
  { to: "/solo?tab=habits", matchSearch: "tab=habits", icon: ListChecks,  label: "Habits", testid: "nav-solo-habits" },
  { to: "/solo?tab=goals",  matchSearch: "tab=goals",  icon: Target,      label: "Goals",  testid: "nav-solo-goals" },
  { to: "/client",       icon: House,         label: "Dashboard",     testid: "nav-gc-dashboard" },
  { to: "/workouts",     icon: Barbell,       label: "Workout",       testid: "nav-gc-workout" },
  { to: "/nutrition",    icon: BowlFood,      label: "Nutrition",     testid: "nav-gc-nutrition" },
  { to: "/social",       icon: Trophy,        label: "Challenges",    testid: "nav-gc-challenges" },
  { to: "/ai-coach",     icon: Brain,         label: "AI Coach",      testid: "nav-gc-ai-coach" },
];

// Pro-only tools for solo (general_client) users. Lifted from the Upgrade
// page's "TRAIN LIKE A PRO" benefit list. When the user isn't Pro, each item
// is rendered locked and clicking routes to /upgrade with a highlight slug
// that scrolls the matching benefit row into view on the Upgrade page.
const soloProNavItems = [
  { to: "/programs",     icon: BookOpen,      label: "Programs",          testid: "nav-gc-pro-programs",    highlightSlug: "unlimited-programs" },
  { to: "/form-check",   icon: Camera,        label: "AI Form Check",     testid: "nav-gc-pro-form-check",  highlightSlug: "form-check" },
  { to: "/analytics",    icon: ChartLine,     label: "Deep Analytics",    testid: "nav-gc-pro-analytics",   highlightSlug: "analytics" },
  { to: "/prs",          icon: Trophy,        label: "PB Charts",         testid: "nav-gc-pro-prs",         highlightSlug: "pb-charts" },
  { to: "/recovery",     icon: MoonStars,     label: "Recovery Insights", testid: "nav-gc-pro-recovery",    highlightSlug: "recovery-insights" },
];

// R3 — Role-specific themes applied via CSS variables + Tailwind arbitrary values.
// Keeps existing dark surface but tints sidebar background, brand stripe and accent.
// `isDark` drives nav text colours so labels stay readable on dark backgrounds
// (fixes user-reported "dark royal blue on dark sidebar" contrast bug).
const ROLE_THEMES = {
  super_admin: {
    stripe: "SUPER ADMIN", stripeBg: "bg-red-500/10 text-red-400 border-red-500/40",
    sidebarBg: "bg-[#0d0e12]", sidebarBorder: "border-red-500/20", brandColor: "text-red-400", isDark: true,
  },
  academy_admin: {
    stripe: "ACADEMY ADMIN", stripeBg: "bg-yellow-400/10 text-yellow-300 border-yellow-400/40",
    sidebarBg: "bg-[#0b0f1a]", sidebarBorder: "border-yellow-400/20", brandColor: "text-yellow-300", isDark: true,
  },
  strength_coach: {
    stripe: "COACH · S&C", stripeBg: "bg-brand-accent/10 text-brand-accent border-brand-accent/40",
    sidebarBg: "bg-surface-DEFAULT", sidebarBorder: "border-slate-200", brandColor: "text-brand-accent", isDark: false,
  },
  fitness_trainer: {
    stripe: "COACH · FITNESS", stripeBg: "bg-lime-400/10 text-lime-300 border-lime-400/40",
    sidebarBg: "bg-[#0a1410]", sidebarBorder: "border-lime-400/20", brandColor: "text-lime-300", isDark: true,
  },
  sports_coach: {
    stripe: "COACH · SPORTS", stripeBg: "bg-cyan-400/10 text-cyan-300 border-cyan-400/40",
    sidebarBg: "bg-[#0a1220]", sidebarBorder: "border-cyan-400/20", brandColor: "text-cyan-300", isDark: true,
  },
  athlete: {
    stripe: "TRAINEE · ATHLETE", stripeBg: "bg-brand-accent/10 text-brand-accent border-brand-accent/40",
    sidebarBg: "bg-surface-DEFAULT", sidebarBorder: "border-slate-200", brandColor: "text-brand-accent", isDark: false,
  },
  general_client: {
    stripe: "TRAINEE · SOLO", stripeBg: "bg-white/10 text-white/80 border-white/20",
    sidebarBg: "bg-[#0f1116]", sidebarBorder: "border-white/15", brandColor: "text-white", isDark: true,
  },
  parent: {
    stripe: "PARENT / GUARDIAN", stripeBg: "bg-amber-400/10 text-amber-300 border-amber-400/40",
    sidebarBg: "bg-[#141018]", sidebarBorder: "border-amber-400/20", brandColor: "text-amber-300", isDark: true,
  },
};

const commonFooterItems = [
  { to: "/profile", icon: User, label: "Profile", testid: "nav-profile" },
];

const upgradeItem = { to: "/upgrade", icon: Sparkle, label: "Upgrade", testid: "nav-upgrade", highlight: true };
// Athletes / clients see an ATHLETE-scoped upgrade page (not coach plans).
const athleteUpgradeItem = { to: "/upgrade?tab=athlete", icon: Sparkle, label: "Upgrade", testid: "nav-upgrade", highlight: true };

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { academy } = useAcademy();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasThreads, setHasThreads] = useState(false);
  const [unread, setUnread] = useState(0);
  const [hasTeams, setHasTeams] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notifUnread, setNotifUnread] = useState(0);
  const [newLeads, setNewLeads] = useState(0);
  const [announcement, setAnnouncement] = useState(null);
  const [dismissedAnnouncementId, setDismissedAnnouncementId] = useState(() => {
    try { return localStorage.getItem("dismissed_announcement") || ""; } catch { return ""; }
  });
  // Auto-close the mobile drawer whenever the route changes so nav-taps feel snappy.
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  useEffect(() => {
    const loadThreads = () => {
      api.get("/messages/threads").then(({ data }) => {
        setHasThreads((data || []).length > 0);
        setUnread((data || []).reduce((s, t) => s + (t.unread_count || 0), 0));
      }).catch(() => {});
    };
    const loadTeams = () => {
      api.get("/my/teams").then(({ data }) => setHasTeams((data || []).length > 0)).catch(() => {});
    };
    const loadNotifCount = () => {
      api.get("/notifications/unread-count").then(({ data }) => {
        setNotifUnread(data?.unread || 0);
      }).catch(() => {});
    };
    const loadAnnouncement = () => {
      api.get("/announcements/active").then(({ data }) => {
        setAnnouncement(data?.announcement || null);
      }).catch(() => {});
    };
    loadThreads();
    loadTeams();
    loadNotifCount();
    loadAnnouncement();
    const iv = setInterval(loadThreads, 20000);
    const nv = setInterval(loadNotifCount, 30000);
    const av = setInterval(loadAnnouncement, 5 * 60 * 1000);
    return () => { clearInterval(iv); clearInterval(nv); clearInterval(av); };
  }, []);

  // Super Admin: surface count of unactioned demo/contact leads on the sidebar.
  const isSuperAdminUser = user?.role === "super_admin" || canonicalRole(user?.primary_role) === "super_admin";
  useEffect(() => {
    if (!isSuperAdminUser) return;
    const loadLeads = () => {
      api.get("/admin/demo-requests?status=new")
        .then(({ data }) => setNewLeads(data?.counts?.new || 0))
        .catch(() => {});
    };
    loadLeads();
    const lv = setInterval(loadLeads, 30000);
    return () => clearInterval(lv);
  }, [isSuperAdminUser, location.pathname, location.search]);

  const dismissAnnouncement = () => {
    if (!announcement) return;
    try { localStorage.setItem("dismissed_announcement", announcement.announcement_id); } catch { /* ignore */ }
    setDismissedAnnouncementId(announcement.announcement_id);
  };
  const showBanner = announcement && (!announcement.dismissible || dismissedAnnouncementId !== announcement.announcement_id);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const messagesItem = hasThreads
    ? [
        { to: "/messages", icon: ChatCircleDots, label: "Messages", testid: "nav-messages", badge: unread },
        { to: "/reviews", icon: VideoCamera, label: "Reviews", testid: "nav-reviews" },
      ]
    : [];

  const athleteTeamsItem = hasTeams
    ? [{ to: "/teams", icon: UsersThree, label: "Teams", testid: "nav-teams" }]
    : [];

  const primaryRole = canonicalRole(user?.primary_role);
  // Merged coach model: S&C, Fitness Trainer, and Sports Coach share the same
  // sidebar; sub-role is surfaced as a specialty pill in the header.
  const COACH_ROLES = ["strength_coach", "fitness_trainer", "sports_coach"];
  const isAcademyCoach = COACH_ROLES.includes(primaryRole) && !!academy;
  const isIndependentCoach = COACH_ROLES.includes(primaryRole) && !academy;

  // Hide the UPGRADE nav item once the user is already on a paid tier.
  // Coach Pro is signalled by `coach_tier === 'coach_pro'` or a future `pro_until`.
  const proUntilRaw = user?.coach_pro_until || user?.pro_until;
  const isProActive =
    user?.coach_tier === "coach_pro" ||
    (!!proUntilRaw && new Date(proUntilRaw).getTime() > Date.now());
  const footerItems = isProActive ? commonFooterItems : [upgradeItem, ...commonFooterItems];
  // Athletes/clients/parents get their own upgrade tab (never the coach one).
  const athleteFooterItems = isProActive ? commonFooterItems : [athleteUpgradeItem, ...commonFooterItems];

  // Strict role-scoped sidebar. Each role gets ONLY the sections it needs.
  let sections = [];
  if (primaryRole === "super_admin") {
    sections = [
      { label: "PLATFORM", items: [
        { to: "/admin?tab=overview",      matchSearch: "tab=overview",      icon: ChartLine,   label: "Overview",      testid: "nav-admin-overview" },
        { to: "/admin?tab=revenue",       matchSearch: "tab=revenue",       icon: CreditCard,  label: "Revenue",       testid: "nav-admin-revenue" },
        { to: "/admin?tab=cohorts",       matchSearch: "tab=cohorts",       icon: Users,       label: "Cohorts",       testid: "nav-admin-cohorts" },
        { to: "/admin?tab=users",         matchSearch: "tab=users",         icon: Users,       label: "Users",         testid: "nav-admin-users" },
        { to: "/admin?tab=academies",     matchSearch: "tab=academies",     icon: Buildings,   label: "Academies",     testid: "nav-admin-academies" },
        { to: "/admin?tab=leads",          matchSearch: "tab=leads",         icon: Envelope,    label: "Leads",         testid: "nav-admin-leads", badge: newLeads, badgeTestid: "nav-leads-badge" },
        { to: "/admin?tab=payments",      matchSearch: "tab=payments",      icon: CreditCard,  label: "Payments",      testid: "nav-admin-payments" },
        { to: "/admin?tab=announcements", matchSearch: "tab=announcements", icon: Lightning,   label: "Announce",      testid: "nav-admin-announcements" },
        { to: "/admin?tab=audit",         matchSearch: "tab=audit",         icon: FileText,    label: "Audit Log",     testid: "nav-admin-audit" },
        { to: "/admin?tab=flags",         matchSearch: "tab=flags",         icon: ShieldStar,  label: "Feature Flags", testid: "nav-admin-flags" },
        { to: "/admin?tab=health",        matchSearch: "tab=health",        icon: Heartbeat,   label: "Health",        testid: "nav-admin-health" },
        { to: "/admin?tab=moderation",    matchSearch: "tab=moderation",    icon: ShieldStar,  label: "Moderation",    testid: "nav-admin-moderation" },
      ] },
      { label: null, items: commonFooterItems },
    ];
  } else if (primaryRole === "academy_admin") {
    // Academy admin manages the org — command deck with deep-links + coach toolkit
    const academyOrgItems = [
      { to: "/academy?tab=overview",      matchSearch: "tab=overview",      icon: Buildings,   label: "Overview",       testid: "nav-academy-overview" },
      { to: "/academy?tab=analytics",     matchSearch: "tab=analytics",     icon: ChartLine,   label: "Analytics",      testid: "nav-academy-analytics" },
      { to: "/academy?tab=attendance",    matchSearch: "tab=attendance",    icon: ListChecks,  label: "Attendance",     testid: "nav-academy-attendance" },
      { to: "/academy?tab=events",        matchSearch: "tab=events",        icon: Calendar,    label: "Events",         testid: "nav-academy-events" },
      { to: "/academy?tab=fees",          matchSearch: "tab=fees",          icon: CurrencyDollar, label: "Fees",        testid: "nav-academy-fees" },
      { to: "/academy?tab=announcements", matchSearch: "tab=announcements", icon: Megaphone,   label: "Announcements",  testid: "nav-academy-announcements" },
      { to: "/academy?tab=invites",       matchSearch: "tab=invites",       icon: Envelope,    label: "Invites",        testid: "nav-academy-invites" },
      { to: "/academy?tab=settings",      matchSearch: "tab=settings",      icon: Gear,        label: "Settings",       testid: "nav-academy-settings" },
    ];
    sections = [
      { label: "ORG", items: academyOrgItems },
      { label: "COACH", items: academyAdminCoachNavItems },
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: null, items: footerItems },
    ];
  } else if (isIndependentCoach) {
    // Independent coach → full coach toolkit + AI program builder for building client programs.
    // Sports Coach specialty gets its own tailored nav (Technical Hub, Drills, Analyzer,
    // Opposition, Match-Day) instead of the S&C toolkit — the S&C tabs (1RM Board,
    // Load Calc, Autoreg, etc.) aren't relevant to a Sports Coach persona.
    const coachItems = primaryRole === "sports_coach" ? sportsCoachNavItems : [...filterCoachNavForRole(coachNavItems, primaryRole), aiProgramBuilderItem];
    sections = [
      { label: "COACH", items: coachItems },
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: "COMMUNITY", items: socialNavItems },
      { label: null, items: footerItems },
    ];
  } else if (isAcademyCoach) {
    // Academy coach → minimal toolkit, only tracks their athletes (no AI Program Builder, no programs)
    const coachItems = primaryRole === "sports_coach" ? sportsCoachNavItems : academyCoachNavItems;
    sections = [
      { label: "ORG", items: [{ to: "/academy", icon: Buildings, label: "My Academy", testid: "nav-academy" }] },
      { label: "COACH", items: coachItems },
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: null, items: commonFooterItems },
    ];
  } else if (primaryRole === "parent") {
    sections = [
      { label: "FAMILY", items: parentNavItems },
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: null, items: commonFooterItems },
    ];
  } else if (primaryRole === "athlete") {
    sections = [
      ...(academy ? [{ label: "ORG", items: [{ to: "/academy", icon: Buildings, label: "My Academy", testid: "nav-academy" }] }] : []),
      ...(athleteTeamsItem.length ? [{ label: "COMPETE", items: athleteTeamsItem }] : []),
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: "TRAIN", items: athleteNavItems },
      { label: "COMMUNITY", items: socialNavItems },
      { label: null, items: athleteFooterItems },
    ];
  } else if (primaryRole === "general_client") {
    // Free tools everyone gets; Pro tools locked behind pro_until.
    const proItems = soloProNavItems.map((it) => ({
      ...it,
      locked: !isProActive,
      // Non-pro users get redirected to the athlete-upgrade tab with a
      // ?highlight=<slug> so the matching benefit row pulses into view.
      to: isProActive
        ? it.to
        : `/upgrade?tab=athlete${it.highlightSlug ? `&highlight=${it.highlightSlug}` : ""}`,
    }));
    sections = [
      { label: "TRAIN", items: generalClientNavItems },
      { label: isProActive ? "PRO" : "PRO · LOCKED", items: proItems },
      ...(messagesItem.length ? [{ label: "COMMS", items: messagesItem }] : []),
      { label: null, items: athleteFooterItems },
    ];
  } else {
    // Fallback for any unknown / legacy role
    sections = [
      { label: "TRAIN", items: individualNavItems },
      { label: null, items: footerItems },
    ];
  }

  const navItems = sections.flatMap((s) => s.items);
  const theme = ROLE_THEMES[primaryRole] || ROLE_THEMES.general_client;

  return (
    <div className="min-h-screen bg-surface-base text-white flex" data-testid="app-layout" data-role={primaryRole}>
      {/* Sidebar */}
      <aside data-theme={theme.isDark ? "dark" : "light"} className={`hidden lg:flex w-60 flex-col border-r ${theme.sidebarBorder} ${theme.sidebarBg} sticky top-0 h-screen ${theme.isDark ? "text-slate-200" : "text-slate-900"}`} data-testid="app-sidebar">
        <Link to="/dashboard" className={`flex items-center gap-2 px-4 h-16 border-b ${theme.sidebarBorder}`} data-testid="sidebar-brand">
          <img src="/icon-192.png" alt="Edge Training" className="h-9 w-9 rounded-lg shadow-md" />
          <span className={`font-heading text-base tracking-tight leading-none ${theme.isDark ? "text-white" : "text-slate-900"}`}>EDGE TRAINING</span>
        </Link>
        <div className={`px-6 py-2 border-b ${theme.sidebarBorder} ${theme.stripeBg} border-t-0 border-x-0 border`} data-testid="role-stripe">
          <div className="label-tiny text-[9px] tracking-[0.2em]">// {theme.stripe}</div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto no-scrollbar">
          {sections.map((section, si) => (
            <div key={si} className={si > 0 ? "mt-4" : ""}>
              {section.label && (
                <div className={`px-6 pb-1 pt-2 label-tiny tracking-widest text-[10px] ${theme.isDark ? "text-slate-500" : "text-text-secondary/60"}`} data-testid={`nav-section-${section.label.toLowerCase()}`}>
                  // {section.label}
                </div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.testid || item.to}
                  to={item.to}
                  data-testid={item.testid}
                  title={item.locked ? "Pro subscribers only — upgrade to unlock" : undefined}
                  className={({ isActive }) => {
                    // Query-param-aware active detection (multiple items share the same pathname).
                    const forceActive = item.matchSearch
                      ? location.search.includes(item.matchSearch) ||
                        (item.matchSearch === "tab=overview" && !location.search && location.pathname === "/admin")
                      : isActive;
                    const activeClasses = theme.isDark
                      ? "text-white bg-white/10 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-brand-accent"
                      : "text-slate-900 bg-slate-100 before:absolute before:left-0 before:top-0 before:bottom-0 before:w-[2px] before:bg-brand-accent";
                    const idleClasses = item.locked
                      ? (theme.isDark
                          ? "text-slate-500 hover:text-brand-heat hover:bg-brand-heat/5"
                          : "text-text-secondary/60 hover:text-brand-heat hover:bg-brand-heat/5")
                      : item.highlight
                        ? "text-brand-accent hover:bg-brand-accent/5"
                        : theme.isDark
                          ? "text-slate-300 hover:text-white hover:bg-white/5"
                          : "text-text-secondary hover:text-slate-900 hover:bg-slate-100";
                    return `flex items-center gap-3 px-6 py-2.5 text-sm label-tiny tracking-wider transition-colors relative ${
                      item.locked ? idleClasses : (forceActive ? activeClasses : idleClasses)
                    }`;
                  }}
                >
                  <item.icon size={18} weight="duotone" />
                  {item.label}
                  {item.badge ? (
                    <span className="ml-auto bg-brand-accent text-surface-base text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular-nums" data-testid={item.badgeTestid || "nav-messages-badge"}>
                      {item.badge}
                    </span>
                  ) : null}
                  {item.locked && (
                    <Lock size={12} weight="fill" className="ml-auto text-brand-heat/70" data-testid={`${item.testid}-lock`} />
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>
        <div className={`p-4 border-t ${theme.sidebarBorder}`}>
          <div className="flex items-center gap-3 mb-3 px-2">
            {user?.picture ? (
              <img src={user.picture.startsWith("/api/") ? `${BACKEND_URL}${user.picture}` : user.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
            ) : (
              <div className="w-8 h-8 rounded-full bg-brand-primary flex items-center justify-center text-xs font-bold text-white">
                {user?.name?.[0]?.toUpperCase() || "?"}
              </div>
            )}
            <div className="flex-1 overflow-hidden">
              <div className={`text-sm font-medium truncate ${theme.isDark ? "text-white" : "text-slate-900"}`} data-testid="sidebar-user-name">{user?.name}</div>
              <div className={`text-xs truncate ${theme.isDark ? "text-slate-400" : "text-text-secondary"}`}>{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onLogout}
            className={`w-full flex items-center justify-center gap-2 border py-2 text-xs label-tiny tracking-wider transition-colors ${
              theme.isDark
                ? "border-white/15 text-slate-300 hover:border-white/40 hover:text-white"
                : "border-slate-300 text-text-secondary hover:border-slate-500 hover:text-slate-900"
            }`}
            data-testid="sidebar-logout-button"
          >
            <SignOut size={14} /> LOG OUT
          </button>
        </div>
      </aside>

      {/* Mobile top nav */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-40 crystal-glass" data-theme={theme.isDark ? "dark" : "light"}>
        <div className="h-14 px-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="mobile-brand">
            <img src="/icon-192.png" alt="Edge Training" className="h-8 w-8 rounded-md" />
            <span className="font-heading text-sm tracking-tight leading-none text-white">EDGE TRAINING</span>
          </Link>
          <button
            onClick={() => setMenuOpen(true)}
            className="relative w-10 h-10 flex items-center justify-center border border-white/15 hover:!border-brand-heat hover:!text-brand-heat transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="Open navigation menu"
          >
            <List size={18} weight="bold" />
            {(notifUnread > 0 || unread > 0) && (
              <span
                className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full !bg-yellow-400 !text-slate-900 text-[9px] font-bold flex items-center justify-center tabular-nums"
                data-testid="mobile-menu-toggle-badge"
              >
                {(() => { const t = (notifUnread || 0) + (unread || 0); return t > 99 ? "99+" : t; })()}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Mobile drawer — full-screen slide-in from the right with the same
          section-based nav as the desktop sidebar. Backdrop dismisses. */}
      {menuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-50 flex"
          data-testid="mobile-drawer"
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={() => setMenuOpen(false)}
            className="flex-1 bg-black/60 backdrop-blur-sm animate-in fade-in-0"
            aria-label="Close menu"
            data-testid="mobile-drawer-backdrop"
          />
          <aside
            data-theme={theme.isDark ? "dark" : "light"}
            className={`w-72 max-w-[85vw] flex flex-col ${theme.sidebarBg} border-l ${theme.sidebarBorder} ${theme.isDark ? "text-slate-200" : "text-slate-900"} animate-in slide-in-from-right duration-200`}
          >
            <div className={`flex items-center justify-between px-4 h-14 border-b ${theme.sidebarBorder}`}>
              <div className="flex items-center gap-2">
                <img src="/icon-192.png" alt="Edge Training" className="h-8 w-8 rounded-md" />
                <span className={`font-heading text-sm tracking-tight leading-none ${theme.isDark ? "text-white" : "text-slate-900"}`}>EDGE TRAINING</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    // Defer so drawer close animation runs first
                    setTimeout(() => window.dispatchEvent(new CustomEvent("open-notifications")), 50);
                  }}
                  className="relative w-9 h-9 flex items-center justify-center hover:!text-yellow-400 transition-colors"
                  data-testid="mobile-drawer-notifications"
                  aria-label="Notifications"
                >
                  <Bell size={18} weight={notifUnread > 0 ? "fill" : "regular"} />
                  {notifUnread > 0 && (
                    <span
                      className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full !bg-yellow-400 !text-slate-900 text-[9px] font-bold flex items-center justify-center tabular-nums"
                      data-testid="mobile-drawer-notifications-badge"
                    >
                      {notifUnread > 99 ? "99+" : notifUnread}
                    </span>
                  )}
                </button>
                <button
                  onClick={() => setMenuOpen(false)}
                  className="w-9 h-9 flex items-center justify-center hover:!text-brand-heat"
                  data-testid="mobile-drawer-close"
                  aria-label="Close menu"
                >
                  <X size={18} weight="bold" />
                </button>
              </div>
            </div>
            <div className={`px-4 py-2 border-b ${theme.sidebarBorder} ${theme.stripeBg} border-t-0 border-x-0 border`}>
              <div className="label-tiny text-[9px] tracking-[0.2em]">// {theme.stripe}</div>
            </div>
            <nav className="flex-1 py-3 overflow-y-auto">
              {sections.map((section, si) => (
                <div key={si} className={si > 0 ? "mt-3" : ""}>
                  {section.label && (
                    <div className={`px-4 pb-1 pt-1 label-tiny tracking-widest text-[10px] ${theme.isDark ? "text-slate-500" : "text-text-secondary/60"}`}>
                      // {section.label}
                    </div>
                  )}
                  {section.items.map((item) => (
                    <NavLink
                      key={item.testid || item.to}
                      to={item.to}
                      data-testid={`${item.testid}-mobile`}
                      onClick={() => setMenuOpen(false)}
                      title={item.locked ? "Pro subscribers only — upgrade to unlock" : undefined}
                      className={({ isActive }) => {
                        const activeClasses = theme.isDark
                          ? "text-white bg-white/10"
                          : "text-slate-900 bg-slate-100";
                        const idleClasses = item.locked
                          ? (theme.isDark ? "text-slate-500 hover:text-brand-heat" : "text-text-secondary/60 hover:text-brand-heat")
                          : item.highlight
                            ? "text-brand-accent hover:bg-brand-accent/5"
                            : theme.isDark
                              ? "text-slate-300 hover:text-white hover:bg-white/5"
                              : "text-slate-600 hover:text-slate-900 hover:bg-slate-100";
                        return `flex items-center gap-3 px-4 py-2.5 text-sm relative ${item.locked ? idleClasses : (isActive ? activeClasses : idleClasses)}`;
                      }}
                    >
                      <item.icon size={16} weight="duotone" />
                      <span>{item.label}</span>
                      {item.badge > 0 && (
                        <span className="ml-auto bg-brand-heat text-surface-base text-[10px] tabular-nums px-1.5 py-0.5 rounded-full font-bold">
                          {item.badge}
                        </span>
                      )}
                      {item.locked && (
                        <Lock size={11} weight="fill" className="ml-auto text-brand-heat/70" />
                      )}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
            <div className={`px-4 py-3 border-t ${theme.sidebarBorder}`}>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 border border-brand-accent flex items-center justify-center text-xs label-tiny">
                  {user?.name?.[0] || "?"}
                </div>
                <div className="min-w-0">
                  <div className="text-sm truncate">{user?.name}</div>
                  <div className="label-tiny text-[10px] text-text-secondary truncate">{user?.email}</div>
                </div>
              </div>
              <button
                onClick={onLogout}
                className={`w-full flex items-center justify-center gap-2 py-2 text-xs label-tiny tracking-widest border transition-colors ${
                  theme.isDark
                    ? "border-white/15 hover:!border-brand-heat hover:!text-brand-heat"
                    : "border-slate-300 hover:!border-brand-heat hover:!text-brand-heat"
                }`}
                data-testid="mobile-drawer-logout"
              >
                <SignOut size={14} /> LOG OUT
              </button>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 pt-14 lg:pt-0">
        {user && <NotificationBell />}
        <KillswitchBanner />
        {showBanner && (
          <div
            className="bg-brand-heat text-white px-4 py-2.5 flex items-center gap-3 text-sm border-b border-brand-heat/50 sticky top-0 z-30"
            data-testid="announcement-banner"
            role="status"
          >
            <Lightning size={14} weight="fill" className="shrink-0" />
            <div className="flex-1 min-w-0">{announcement.message}</div>
            {announcement.cta_text && announcement.cta_link && (
              <Link
                to={announcement.cta_link}
                className="underline underline-offset-2 label-tiny text-[11px] tracking-wider hover:!text-white/90 whitespace-nowrap"
                data-testid="announcement-banner-cta"
              >
                {announcement.cta_text} →
              </Link>
            )}
            {announcement.dismissible && (
              <button
                onClick={dismissAnnouncement}
                className="p-1 hover:!text-white/70 transition-colors"
                data-testid="announcement-banner-dismiss"
                aria-label="Dismiss"
              >
                <X size={12} weight="bold" />
              </button>
            )}
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
